import type { RouteComponentProps } from "@reach/router";

import { useCompanyPreviewData } from "../hooks/useCompanyPreviewData";
import type { CompanyLocation } from "newui/features/Company/types";

type OfficeLocationsTabProps = RouteComponentProps;

const LocationRow: React.FC<{ location: CompanyLocation }> = ({ location }) => (
	<>
		{/* Desktop: grid row */}
		<div className="hidden grid-cols-12 border-b border-gray-100 py-3 text-sm md:grid">
			<div className="col-span-3 px-3 text-gray-900">
				{location.address || <>&mdash;</>}
			</div>
			<div className="col-span-2 border-l border-gray-100 px-3 text-gray-900">
				{location.postcode || <>&mdash;</>}
			</div>
			<div className="col-span-2 border-l border-gray-100 px-3 text-gray-900">
				{location.city || <>&mdash;</>}
			</div>
			<div className="col-span-2 border-l border-gray-100 px-3 text-gray-900">
				{location.state || <>&mdash;</>}
			</div>
			<div className="col-span-3 border-l border-gray-100 px-3 text-gray-900">
				{location.local_govt_association?.name || <>&mdash;</>}
			</div>
		</div>

		{/* Mobile: stacked card */}
		<div className="rounded-lg border border-gray-100 p-3 md:hidden">
			<div className="space-y-1 text-sm">
				{location.address && (
					<p className="text-gray-900">{location.address}</p>
				)}
				<div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-500">
					{location.city && <span>{location.city}</span>}
					{location.state && <span>{location.state}</span>}
					{location.postcode && <span>{location.postcode}</span>}
				</div>
				{location.local_govt_association?.name && (
					<p className="text-xs text-gray-400">
						LGA: {location.local_govt_association.name}
					</p>
				)}
			</div>
		</div>
	</>
);

const LocationGridHeader: React.FC = () => (
	<div className="hidden grid-cols-12 md:grid">
		<div className="col-span-3 px-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
			Address
		</div>
		<div className="col-span-2 px-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
			Postcode
		</div>
		<div className="col-span-2 px-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
			City/Suburb
		</div>
		<div className="col-span-2 px-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
			State
		</div>
		<div className="col-span-3 px-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
			Local Government
		</div>
	</div>
);

export const OfficeLocationsTab: React.FC<OfficeLocationsTabProps> = () => {
	const { company } = useCompanyPreviewData();
	const locations = company?.locations ?? [];

	if (locations.length === 0) {
		return (
			<p className="py-8 text-center text-sm text-gray-500">
				No office locations available.
			</p>
		);
	}

	const mainOffice = locations[0];
	const secondaryOffices = locations.slice(1);

	return (
		<div className="space-y-8">
			{/* Main Office */}
			<section>
				<h3 className="mb-3 text-base font-semibold text-gray-900">
					Main Office
				</h3>
				<LocationGridHeader />
				<div className="mt-2 space-y-2 md:space-y-0">
					<LocationRow location={mainOffice} />
				</div>
			</section>

			{/* Secondary Offices */}
			{secondaryOffices.length > 0 && (
				<section>
					<h3 className="mb-3 text-base font-semibold text-gray-900">
						Secondary Offices
					</h3>
					<LocationGridHeader />
					<div className="mt-2 space-y-2 md:space-y-0">
						{secondaryOffices.map((location) => (
							<LocationRow key={location.id} location={location} />
						))}
					</div>
				</section>
			)}
		</div>
	);
};
